import { supabase } from './supabase';
import { PLANS, getPlan, isValidPlanId } from '../lib/plans';

const PRODUCT_TYPES = [
  // Current trial = subscription with £1 first month → £4.99/mo auto-renew
  'subscription_pro_trial',
  // Other tiers
  'lifetime_access',
  'subscription_pro',
  // Legacy / intermediate types still recognized for back-compat
  'trial_unlock_once',          // old one-time-£1-then-consumed model
  'subscription_pro_intro',
  'subscription_intro_charge',
  'design_inspiration_access',
];

const TEST_USER_EMAILS = (process.env.REACT_APP_TEST_USER_EMAILS || '')
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

function isTestUserEmail(email) {
  if (!email) return false;
  return TEST_USER_EMAILS.includes(email.toLowerCase());
}

class PaymentService {
  /**
   * Starts a Stripe Checkout session for a specific plan.
   * @param {('trial'|'monthly'|'lifetime')} planId
   * @param {string} origin
   * @param {string} context
   * @returns {Promise<{sessionId: string, url: string}>}
   */
  async startCheckout(planId, origin = window.location.origin, context = 'project-creation') {
    if (!isValidPlanId(planId)) {
      throw new Error(`Unknown plan: ${planId}`);
    }

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('User must be authenticated to proceed with payment');
    }

    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: { plan: planId, origin, context },
    });

    if (error) {
      console.error('Error creating checkout session:', error);
      throw new Error(error.message || 'Failed to create checkout session');
    }

    return data; // { sessionId, url }
  }

  /**
   * Backward-compatible wrapper. Defaults to the monthly plan.
   */
  async startIntroSubscription(origin = window.location.origin, context = 'project-creation') {
    return this.startCheckout('monthly', origin, context);
  }

  /**
   * Marks the user's most recent unconsumed £1 trial unlock as consumed.
   * Idempotent — safe to call after every project download.
   */
  async markTrialConsumed() {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) return { success: false };

      const { data, error } = await supabase.functions.invoke('consume-trial-unlock', {
        body: {},
      });
      if (error) {
        console.warn('Failed to mark trial consumed:', error);
        return { success: false };
      }
      return data || { success: true };
    } catch (err) {
      console.warn('markTrialConsumed error:', err);
      return { success: false };
    }
  }

  async cancelSubscription(subscriptionId) {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) throw new Error('User must be authenticated');

      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { subscription_id: subscriptionId || null },
      });
      if (error) throw new Error(error.message || 'Failed to cancel subscription');
      return data;
    } catch (error) {
      console.error('Cancel subscription error:', error);
      throw error;
    }
  }

  /**
   * Returns the user's current access state.
   * Result: { hasPaid, accessTier, lastPayment, payments }
   *   accessTier ∈ 'lifetime' | 'monthly' | 'trial' | 'test' | null
   */
  async getPaymentStatus() {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        return { hasPaid: false, accessTier: null, lastPayment: null, payments: [] };
      }

      // Layer 1: test-user allowlist short-circuits everything.
      if (isTestUserEmail(user.email)) {
        console.log('🧪 Test user email matched — granting test access');
        return {
          hasPaid: true,
          accessTier: 'test',
          lastPayment: null,
          payments: [],
        };
      }

      const { data: payments, error } = await supabase
        .from('user_payments')
        .select('*')
        .eq('user_id', user.id)
        .in('product_type', PRODUCT_TYPES)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching payment status:', error);
        return { hasPaid: false, accessTier: null, lastPayment: null, payments: [] };
      }

      const list = payments || [];

      const lifetime = list.find(
        (p) => p.product_type === 'lifetime_access' && p.payment_status === 'completed'
      );
      if (lifetime) {
        return { hasPaid: true, accessTier: 'lifetime', lastPayment: lifetime, payments: list };
      }

      // Active subscriptions (£1 trial, regular monthly, and legacy intro types).
      // Any 'subscription_created' row whose stripe_subscription_id has NOT been
      // later marked 'subscription_cancelled' counts as active access.
      const SUBSCRIPTION_PRODUCT_TYPES = [
        'subscription_pro',
        'subscription_pro_trial',
        'subscription_pro_intro',
      ];
      const subRows = list.filter(
        (p) =>
          SUBSCRIPTION_PRODUCT_TYPES.includes(p.product_type) &&
          p.payment_status === 'subscription_created'
      );
      const cancelledSubIds = new Set(
        list
          .filter((p) => p.payment_status === 'subscription_cancelled')
          .map((p) => p.metadata?.stripe_subscription_id)
          .filter(Boolean)
      );
      const activeSub = subRows.find((p) => {
        const subId = p.metadata?.stripe_subscription_id;
        return !subId || !cancelledSubIds.has(subId);
      });
      if (activeSub) {
        // Tier label: 'trial' while on the £1 intro month, 'monthly' otherwise.
        const tier = activeSub.product_type === 'subscription_pro_trial' ? 'trial' : 'monthly';
        return { hasPaid: true, accessTier: tier, lastPayment: activeSub, payments: list };
      }

      // Recurring invoice payments also keep access active for the current period.
      const completedSubInvoice = list.find(
        (p) =>
          SUBSCRIPTION_PRODUCT_TYPES.includes(p.product_type) &&
          p.payment_status === 'completed'
      );
      if (completedSubInvoice) {
        const tier = completedSubInvoice.product_type === 'subscription_pro_trial' ? 'trial' : 'monthly';
        return {
          hasPaid: true,
          accessTier: tier,
          lastPayment: completedSubInvoice,
          payments: list,
        };
      }

      // Legacy: unconsumed one-time £1 trial (old model). Kept for back-compat.
      const legacyTrial = list.find(
        (p) =>
          p.product_type === 'trial_unlock_once' &&
          p.payment_status === 'completed' &&
          !(p.metadata && p.metadata.consumed === true)
      );
      if (legacyTrial) {
        return { hasPaid: true, accessTier: 'trial', lastPayment: legacyTrial, payments: list };
      }

      // Legacy one-time access.
      const legacy = list.find(
        (p) => p.product_type === 'design_inspiration_access' && p.payment_status === 'completed'
      );
      if (legacy) {
        return { hasPaid: true, accessTier: 'lifetime', lastPayment: legacy, payments: list };
      }

      return { hasPaid: false, accessTier: null, lastPayment: list[0] || null, payments: list };
    } catch (error) {
      console.error('Error fetching payment status:', error);
      return { hasPaid: false, accessTier: null, lastPayment: null, payments: [] };
    }
  }

  async hasUserPaid() {
    const status = await this.getPaymentStatus();
    return status.hasPaid;
  }

  /**
   * Returns true when the user has ever used the £1 trial — whether through the
   * new subscription-based intro coupon (`subscription_pro_trial`) or the legacy
   * one-time £1 unlock (`trial_unlock_once`). Stripe enforces one-coupon-per-
   * customer for `duration: 'once'` coupons, so we mirror that on our side by
   * hiding the trial option once they've taken it.
   */
  async hasUsedTrial() {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) return false;
      if (isTestUserEmail(user.email)) return false;

      const { data, error } = await supabase
        .from('user_payments')
        .select('id, product_type, payment_status')
        .eq('user_id', user.id)
        .in('product_type', ['subscription_pro_trial', 'trial_unlock_once'])
        .limit(5);

      if (error) {
        console.error('Error checking trial usage:', error);
        return false;
      }
      if (!Array.isArray(data) || data.length === 0) return false;
      // Any record at all (even cancelled) means they've used their trial.
      return data.some((row) => {
        if (row.product_type === 'trial_unlock_once') return row.payment_status === 'completed';
        // subscription_pro_trial: completed OR subscription_created OR cancelled = used
        return ['completed', 'subscription_created', 'subscription_cancelled'].includes(row.payment_status);
      });
    } catch (err) {
      console.error('hasUsedTrial error:', err);
      return false;
    }
  }

  async verifyCheckoutSession(sessionId) {
    try {
      const { data, error } = await supabase.functions.invoke('verify-checkout-session', {
        body: { session_id: sessionId },
      });
      if (error) {
        console.warn('verify-checkout-session edge function error:', error);
        return await this.hasUserPaid();
      }
      if (data?.verified) return true;
      return await this.hasUserPaid();
    } catch (error) {
      console.error('Error verifying checkout session:', error);
      return false;
    }
  }

  storePaymentAttempt(sessionId) {
    const paymentData = {
      sessionId,
      timestamp: new Date().toISOString(),
      status: 'pending',
    };
    localStorage.setItem('designInspiration_payment', JSON.stringify(paymentData));
  }

  getStoredPaymentAttempt() {
    try {
      const stored = localStorage.getItem('designInspiration_payment');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading stored payment attempt:', error);
      return null;
    }
  }

  clearStoredPaymentAttempt() {
    localStorage.removeItem('designInspiration_payment');
  }

  updateStoredPaymentStatus(status) {
    const stored = this.getStoredPaymentAttempt();
    if (stored) {
      stored.status = status;
      stored.updatedAt = new Date().toISOString();
      localStorage.setItem('designInspiration_payment', JSON.stringify(stored));
    }
  }
}

export const paymentService = new PaymentService();
export { PLANS, getPlan };
export default paymentService;
